import {
  CloneNfcHashesRequestSchema,
  CloneNfcHashesResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { post } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { type z } from "zod";

type CloneNfcHashesResponse = z.output<typeof CloneNfcHashesResponseSchema>;

interface CloneNfcHashesParams {
  /** Target workplace that should receive the cloned NFC hashes. */
  targetWorkplaceId: string;
  /** Source workplace whose active NFC hashes are cloned. */
  sourceWorkplaceId: string;
}

/**
 * Mutation that clones the source workplace's active NFC hashes into a target
 * workplace via `POST /workplaces/:workplaceId/nfc-hashes/clone`. The cloned
 * tags are created in SET_UP status so the same physical posters work for both
 * workplaces across a change-of-ownership migration.
 */
export function useCloneNfcHashes(
  options: UseMutationOptions<CloneNfcHashesResponse, Error, CloneNfcHashesParams> = {}
) {
  return useMutation({
    mutationFn: async (params: CloneNfcHashesParams) => {
      const { targetWorkplaceId, sourceWorkplaceId } = params;

      const response = await post({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${targetWorkplaceId}/nfc-hashes/clone`,
        data: { sourceWorkplaceId },
        bodySchema: CloneNfcHashesRequestSchema,
        responseSchema: CloneNfcHashesResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CLONE_NFC_HASHES_FAILURE,
    },
    ...options,
  });
}
